import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    ElementRef,
    HostBinding,
    Inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    ViewChild,
    inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HAMMER_GESTURE_CONFIG, HammerGestureConfig } from '@angular/platform-browser';

import { Fixture } from '@cds/betting-offer';
import { Region, Sport } from '@cds/betting-offer/tags';
import { EventModel, EventParticipantImage } from '@frontend/sports/betting-offer/feature/model';
import { MarqueeConfig } from '@frontend/sports/common/client-config-data-access';
import { ScrollAdapterComponent, ScrollSpeed } from '@frontend/sports/common/core/feature/scroll-adapter';
import { CssSupportService } from '@frontend/sports/common/core/utils/css-support';
import { StringDictionary } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { EventSubscription, EventSubscriptionRequest } from '@frontend/sports/event-subscription/feature/base-subscription';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import { SportUrlParam, UrlHelperService } from '@frontend/sports/navigation/core/feature/url-helper';
import { WinGameFixture } from '@frontend/sports/racing/feature/models';
import { MarketType, MarqueeBackgroundContainerConfig, MarqueeType, TitleIconOptions } from '@frontend/sports/types/components/content';
import { MarqueeLink, MarqueeLinkTarget } from '@frontend/sports/types/components/widget/types';
import { NativeAppService, TimerService } from '@frontend/vanilla/core';
import { RxStrategyProvider } from '@rx-angular/cdk/render-strategies';
import produce from 'immer';
import { clone } from 'lodash-es';
import { Subject, fromEvent, takeUntil } from 'rxjs';

import { Promotion, TrackingSource } from '../crm-offer-data/crm-offer.model';
import { CrmPromotionProviderService, PromotionMap } from '../crm-promotion/crm-promotion-provider.service';
import { MarqueeEventSubscriptionService } from '../event-subscription/marquee-event-subscription.service';
import { ColumnLayoutService } from '../layout/column-layout.service';
import { FixtureUpdateService } from '../racing/common/fixture-update.service';
import { HighlightsMarqueePage } from './highlights-marquee-page.enum';
import {
    BetBuilderTile,
    FixtureTile,
    MarqueeTitleIcon,
    Tile,
    TileOrPromotion,
    TileWithFallbackMarketTypes,
    isRacingTile,
    isTile,
} from './marquee-tile.model';
import { MarqueeTrackingService } from './marquee-tracking.service';

@Component({
    selector: 'ms-highlights-marquee',
    templateUrl: 'highlights-marquee.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class HighlightsMarqueeComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
    @Input() sportId?: number;
    @Input() tiles: TileOrPromotion[];
    @Input() promotionTiles: Promotion[] = [];
    @Input() preventNavigation = false;
    @Input() showLoader = false;
    @Input() scrollingSpeed: { [key: string]: string };
    @Input() promotionTrackingSource = TrackingSource.Teaser;
    @Input() page?: HighlightsMarqueePage;
    @Input() backgroundContainerConfig: MarqueeBackgroundContainerConfig;
    @Input() globalLinkConfig: MarqueeLink;
    @Input() allowedSportsWithLogo: number[] = [];

    @HostBinding('class.single-card') isSingleMarquee = false;
    @HostBinding('class.marquee-container') showBackgroundContainer = false;
    @ViewChild(ScrollAdapterComponent) scrollAdapter?: ScrollAdapterComponent;
    hasTiles: boolean;
    isEmpty: boolean;
    scrollSpeed: ScrollSpeed;
    titleIcon: MarqueeTitleIcon;
    promotedFixtureIdsMap: StringDictionary<string> = {};
    crmPromotionClass: string;
    marqueeTiles: Tile[] = [];
    marketType: MarketType;
    private subscription?: EventSubscription<EventModel>;
    private promoLength = 0;
    tilesWithFallbackMarketType: TileWithFallbackMarketTypes[] = [];

    protected timerService = inject(TimerService);
    private ngZone = inject(NgZone);
    private readonly destroyRef = inject(DestroyRef);
    private destroyRacingTileSubscription$ = new Subject<void>();
    private fixtureUpdateService = inject(FixtureUpdateService, { optional: true });

    constructor(
        protected marqueeConfig: MarqueeConfig,
        protected subscriptionService: MarqueeEventSubscriptionService,
        protected columnLayoutService: ColumnLayoutService,
        protected crmPromotionProvider: CrmPromotionProviderService,
        protected marqueeTrackingService: MarqueeTrackingService,
        protected elementRef: ElementRef,
        @Inject(HAMMER_GESTURE_CONFIG) protected hammerGestureConfig: HammerGestureConfig,
        protected cssSupportService: CssSupportService,
        protected nativeAppService: NativeAppService,
        protected urlHelper: UrlHelperService,
        protected rxStrategy: RxStrategyProvider,
        protected cdr: ChangeDetectorRef,
    ) {
        this.crmPromotionProvider.promotionsMap$.pipe(takeUntilDestroyed()).subscribe((ids) => this.promotionMapChanged(ids));
    }

    ngOnInit(): void {
        this.crmPromotionClass = this.hasTiles ? 'crm-promo-card-marquee' : 'crm-promo-card-banner';
        if (this.hasTiles) {
            this.timerService.scheduleIdleCallback(() => {
                const marqueeTiles = this.tiles.filter(isTile);
                this.marqueeTrackingService.trackShowMarquees(marqueeTiles);
            });
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }

    ngAfterViewInit(): void {
        if (this.nativeAppService.isTerminal) {
            this.scrollAdapter?.setArrows();
        } else {
            this.columnLayoutService.rightColumnSizeChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                if (this.scrollAdapter) {
                    this.scrollAdapter.setArrows();
                }
            });
        }
        const scrollElement: HTMLElement | null = this.elementRef.nativeElement.querySelector('ms-scroll-adapter');
        if (!scrollElement || !this.cssSupportService.isTouchActionSupported()) {
            return;
        }

        const hammer = this.hammerGestureConfig.buildHammer(scrollElement);
        fromEvent(hammer, 'swiperight')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event: any) => this.groupsSwiped(event));
    }

    getTileImages(tile: Tile): EventParticipantImage[] {
        const images: EventParticipantImage[] = [];
        if (this.allowedSportsWithLogo.includes(tile.sportId)) {
            tile.event.participants.forEach((p) => {
                images.push({ jersey: p.id + '_1', logo: p.id + '_logo' });
            });
        }

        return images;
    }

    ngOnChanges(changes: ISimpleChanges<HighlightsMarqueeComponent>): void {
        if (changes.tiles) {
            this.marqueeTiles = this.tiles.filter(isTile);
            this.tilesWithFallbackMarketType = this.marqueeTiles;
            this.resubscribe();

            if (!changes.tiles.firstChange) {
                this.isEmpty = this.tiles && this.tiles.length === 0;
            }
        }

        this.refreshData();
        if (this.tiles?.length && this.backgroundContainerConfig) {
            this.populateHeaderData(this.backgroundContainerConfig.regularFixture);
        }

        this.showBackgroundContainer = this.backgroundContainerConfig?.showTitle && !!this.backgroundContainerConfig?.title && this.hasTiles;

        if ((changes.tiles || changes.promotionTiles) && this.scrollAdapter) {
            this.scrollAdapter.resetArrows();
        }

        this.promoLength = this.promotionTiles?.length ?? 0;
    }

    triggerFallbackTile(fallbackTile: { tile: Tile; marketType: string }): void {
        this.tilesWithFallbackMarketType[fallbackTile.tile.index].marketType = fallbackTile.marketType;
    }

    track(index: number, tile: FixtureTile | BetBuilderTile): void {
        if (this.page === HighlightsMarqueePage.BetbuilderCarousel) {
            this.marqueeTrackingService.trackBetBuilderCard();
        }
        this.marqueeTrackingService.trackEventClicks(index, tile, this.page);
    }

    isTile(tile: TileOrPromotion): tile is Tile {
        return 'marqueeType' in tile;
    }

    isTileType(tile: Tile, type: 'Special' | 'Regular'): tile is FixtureTile;
    isTileType(tile: Tile, type: 'BuildABet'): tile is BetBuilderTile;
    isTileType(tile: Tile, type: keyof typeof MarqueeType): boolean {
        return tile.marqueeType === type;
    }

    isPromotion(tile: TileOrPromotion): tile is Promotion {
        return 'id' in tile;
    }

    trackTile(tile: TileOrPromotion): string {
        // WARNING: do not use this, as this is bound to the iterable context rather to the class
        if ('id' in tile) {
            return tile.id;
        }

        return `${tile.fixtureId}-${tile.marqueeType === MarqueeType.Special}-${tile.marqueeType === MarqueeType.BuildABet}-${tile.source?.imageUrl}`;
    }

    getType(tile: TileOrPromotion): string | undefined {
        if (tile && 'marqueeType' in tile) {
            return tile.marqueeType;
        }

        return;
    }

    getEvent(tile: TileOrPromotion): string {
        if (tile && 'event' in tile) {
            return tile.event.id;
        }

        return '';
    }

    setClass(index: number): string {
        const tile = this.tilesWithFallbackMarketType[index];
        const marqueeType = this.getType(tile);
        const promoClass = marqueeType ? 'no-promotion-card' : 'crm-promo-card crm-promo-card-marquee';
        const sixPackClass = this.sixpackOrRegularClass(tile);
        const regularClass = marqueeType === MarqueeType.Regular && tile?.marketType !== MarketType.SixPack ? 'marquee-regular-card' : '';
        const imageClass = tile ? this.getMarqueeImageClass(tile) : '';
        const withLinkClass = tile ? this.getHasLinkClass(tile) : '';

        return [promoClass, sixPackClass, regularClass, imageClass, withLinkClass].join(' ');
    }

    private sixpackOrRegularClass(tile: Tile): string {
        const fallbackTileInfo = this.tilesWithFallbackMarketType.find((f) => f.fixtureId === tile.fixtureId && f.index === tile.index);

        let sixPackClass = '';
        if (fallbackTileInfo && tile.marketType === MarketType.SixPack) {
            sixPackClass = fallbackTileInfo.marketType === MarketType.SixPack ? 'six-pack-card' : 'marquee-regular-card';
        }

        return sixPackClass;
    }

    getMarqueeImageClass(tile: TileOrPromotion): string | undefined {
        return isTile(tile) ? (tile.source?.imageUrl ? 'marquee-card-with-img' : 'marquee-card-without-img') : undefined;
    }

    getListCardClass(): string {
        return this.showBackgroundContainer ? 'marquee-container-cards' : 'cards';
    }

    getHasLinkClass(tile: TileOrPromotion): string | undefined {
        return isTile(tile) && (!this.preventNavigation || tile.source?.link || tile.event) ? 'with-link' : undefined;
    }

    get isLoaderVisible(): boolean {
        return !this.hasTiles && this.showLoader && !this.isEmpty;
    }

    tileIndex(index: number): number {
        return this.promoLength + index;
    }

    private groupsSwiped(event: HammerInput): void {
        event.srcEvent.stopPropagation();
    }

    private promotionMapChanged(promoMap: PromotionMap): void {
        this.promotedFixtureIdsMap = promoMap.fixtures;
        this.rxStrategy.scheduleCD(this.cdr, { scope: this, patchZone: this.ngZone });
    }

    private unsubscribe(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
        this.destroyRacingTileSubscription$.next();
    }

    private resubscribe(): void {
        this.unsubscribe();

        const subscription: EventSubscriptionRequest<EventModel>[] = [];

        this.tiles.forEach((tile, index) => {
            if (isTile(tile)) {
                if (tile.event) {
                    subscription.push({
                        event: tile.event,
                        callback: this.getCallback(index),
                    });
                } else {
                    const fixtureTile = tile as FixtureTile;
                    if (fixtureTile.racingEvent) {
                        this.fixtureUpdateService
                            ?.handleFixtureUpdates(fixtureTile.racingEvent)
                            .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.destroyRacingTileSubscription$))
                            .subscribe(this.getCallback(index));
                    }
                }
            }
        });

        this.subscription = this.subscriptionService.subscribe(subscription);
    }

    private getCallback(index: number): (event: EventModel | WinGameFixture) => void {
        return (event) => {
            this.tiles[index] = produce(this.tiles[index], (draft: TileOrPromotion) => {
                if (isTile(draft)) {
                    if (draft.event?.id === event.id) {
                        draft.event = clone(event as EventModel);
                    } else if (isRacingTile(draft) && draft.racingEvent!.id === event.id) {
                        const optionGroup = draft.racingEvent!.optionGroups?.find((o) => o.id === draft.optionGroupId.toString());
                        const eventOptionGroup = (event as WinGameFixture).optionGroups?.find((o) => o.id === draft.optionGroupId.toString());

                        if (optionGroup?.online || eventOptionGroup?.online) {
                            draft.racingEvent = clone(event as WinGameFixture);
                        }
                    }
                }
            });
            this.rxStrategy.scheduleCD(this.cdr, { scope: this, patchZone: this.ngZone });
        };
    }

    private refreshData(): void {
        this.hasTiles = this.tiles && this.tiles.length !== 0;
        this.isSingleMarquee = this.hasTiles && this.tiles.length === 1;
        this.scrollSpeed = new ScrollSpeed(this.scrollingSpeed || this.marqueeConfig.scrollingSpeed);
    }

    private populateHeaderData(fixturesData: Fixture | undefined): void {
        if (fixturesData) {
            if (!this.backgroundContainerConfig.title) {
                this.backgroundContainerConfig.title = this.backgroundContainerConfig.showTitle ? fixturesData?.name?.value : '';
            }
        }
        this.setGlobalLink(fixturesData);
        this.titleIcon = this.getDataForTitleIcon(fixturesData);
    }

    private setGlobalLink(firstFixture: Fixture | undefined): void {
        if (firstFixture) {
            if (this.globalLinkConfig.visible && !this.globalLinkConfig.linkOverride) {
                switch (this.globalLinkConfig.target) {
                    case MarqueeLinkTarget.SportLobby:
                        const sportParam = firstFixture
                            ? {
                                  id: firstFixture?.sport?.id,
                                  name: firstFixture?.sport?.name?.value,
                              }
                            : undefined;
                        this.globalLinkConfig.linkOverride = this.urlHelper.getSportUrl(sportParam);
                        break;
                    case MarqueeLinkTarget.CompetitionLobby:
                        this.globalLinkConfig.linkOverride = this.getCompetitonData(firstFixture);
                        break;
                    case MarqueeLinkTarget.CompetitionSportPage:
                        this.globalLinkConfig.linkOverride = this.getCompetitionURL(firstFixture.sport, firstFixture.region);
                        break;
                    default:
                        const fixtureParam = firstFixture
                            ? {
                                  id: firstFixture.id,
                                  name: firstFixture.name.value,
                              }
                            : undefined;
                        this.globalLinkConfig.linkOverride = this.urlHelper.getEventUrl(fixtureParam);
                }
            }
        }
    }

    private getCompetitonData(firstFixture: Fixture): string {
        return this.getCompetitionURL(
            firstFixture.sport,
            firstFixture.region,
            firstFixture.competition.id.toString(),
            firstFixture.competition.name.value,
        );
    }

    private getCompetitionURL(sport: Sport, region: Region, competitionId?: string, competitionName?: string): string {
        const sportDetail = {
            id: sport?.id,
            name: sport?.name?.value,
        };
        const regionDetail = {
            id: region?.id,
            name: region?.name?.value,
        };
        let competitionDetail: UrlParam | undefined;
        if (competitionId) {
            competitionDetail = {
                id: competitionId,
                name: competitionName ?? '',
            };

            return this.urlHelper.getSportUrl(sportDetail, SportUrlParam.Betting, regionDetail, competitionDetail);
        }

        return this.urlHelper.getSportUrl(sportDetail, SportUrlParam.Betting, regionDetail);
    }

    private getDataForTitleIcon(fixture: Fixture | undefined): MarqueeTitleIcon {
        if (fixture) {
            switch (this.backgroundContainerConfig.titleIconDisplay) {
                case TitleIconOptions.SportIcon:
                    return {
                        id: fixture.sport.id.toString(),
                        title: fixture?.sport?.name?.value,
                        icon: `sports-${fixture.sport.id}`,
                    };
                case TitleIconOptions.CompetitionLogo:
                case TitleIconOptions.CompetitionFlag:
                    return {
                        id: fixture.competition.id.toString(),
                        title: fixture?.competition?.name?.value,
                        icon: `country-icon c${fixture.region.id}`,
                        sportId: fixture.sport.id,
                    };
            }
        }

        return { id: '', title: '' };
    }
}
